import { marked, Token, Tokens } from 'marked';
import { TooltipModel } from '../data/types';
import { FileHelpers } from './fileHelpers';
type GroupedTokens = {
  parentToken: Tokens.Heading | Tokens.Generic;
  childTokens: Token[];
};
type GroupedTokensWithHeader = GroupedTokens & { key: string; heading: string };

export class MdHelpers {
  static getGroupedTokensByHeaders(content: string) {
    const tokens = marked.lexer(content);
    const tooltips = tokens.reduce<{
      currentTooltip: GroupedTokens | null;
      result: GroupedTokens[];
    }>(
      (acc, current, i, arr) => {
        if (i === arr.length - 1 && acc.currentTooltip !== null) {
          acc.currentTooltip.childTokens.push(current);
          acc.result.push(acc.currentTooltip);
          acc.currentTooltip = null;
        }

        if (current.type === 'heading' && current.depth === 1) {
          if (acc.currentTooltip !== null) {
            acc.result.push(acc.currentTooltip);
          }

          acc.currentTooltip = { parentToken: current, childTokens: [] };
        }

        acc.currentTooltip?.childTokens.push(current);

        return acc;
      },
      { currentTooltip: null, result: [] },
    );
    return tooltips.result;
  }

  static groupHeadersByKeys(groups: GroupedTokens[], keyRegex: RegExp) {
    const reduction = groups.reduce<{
      currentTooltip: GroupedTokensWithHeader | null;
      result: GroupedTokensWithHeader[];
    }>(
      (acc, current) => {
        const headingText = `${current.parentToken.text}`;
        const keyPosition = headingText.search(keyRegex);
        if (keyPosition < 0 && acc.currentTooltip !== null) {
          acc.currentTooltip.childTokens = [
            ...acc.currentTooltip.childTokens,
            ...current.childTokens,
          ];
          return acc;
        }

        if (acc.currentTooltip !== null) {
          acc.result = [...acc.result, acc.currentTooltip];
        }

        const keyContainer = headingText.match(keyRegex)?.[0] ?? '';
        const key = keyContainer.slice(2, -1);
        const heading = headingText.slice(0, keyPosition).trim();
        acc.currentTooltip = {
          parentToken: current.parentToken,
          childTokens: current.childTokens.slice(1),
          key,
          heading,
        };
        return acc;
      },
      { currentTooltip: null, result: [] },
    );
    return reduction.currentTooltip === null
      ? reduction.result
      : [...reduction.result, reduction.currentTooltip];
  }

  static getHtmlElementForHtmlString(
    markdown: string,
    classNames: { tagName: string; className: string }[],
  ) {
    const fragment = document.createElement('div');
    fragment.innerHTML = markdown;
    classNames.map(({ tagName, className }) =>
      setClassesForElementType(fragment, tagName, className),
    );
    return fragment.innerHTML;
  }

  static defaultMarkdownStyles: Parameters<
    typeof this.getHtmlElementForHtmlString
  >[1] = [
    { tagName: 'p', className: 'text-base text-inkMuted py-1' },
    {
      tagName: 'li',
      className:
        'text-base text-inkMuted py-1 list-inside list-decimal ml-3 *:inline',
    },
    {
      tagName: 'h1',
      className:
        'text-3xl font-bold text-primaryColor py-3 border-solid border-b-2 border-primaryColor',
    },
    { tagName: 'h2', className: 'text-base font-bold text-inkMuted py-1' },
    { tagName: 'em', className: 'italic' },
  ];

  /**
   * Chooses what a tooltip surface should render: the freshly-fetched DB
   * content when available (so in-app edits show on return), falling back to
   * a caller-provided pre-rendered `content` only until that fetch resolves.
   */
  static resolveTooltipInnerHtml(
    tooltip: TooltipModel | null,
    content?: string,
  ): string {
    if (tooltip) {
      return MdHelpers.getHtmlElementForHtmlString(
        tooltip.htmlBody ?? '',
        MdHelpers.defaultMarkdownStyles,
      );
    }
    return content ?? '';
  }

  static getTooltipMarkdownFile(tooltips: TooltipModel[]): string {
    // Normalised, idempotent layout: re-exporting an unchanged file yields
    // byte-identical output (bodies trimmed, single blank line as separator,
    // no control characters).
    return tooltips
      .map((tooltip) => {
        const heading = repairControlCharacters(tooltip.heading);
        const headingLine = heading
          ? `# ${heading} {#${tooltip.key}}`
          : `# {#${tooltip.key}}`;
        const body = repairControlCharacters(tooltip.markdownBody).trim();
        return body ? `${headingLine}\n\n${body}\n` : `${headingLine}\n`;
      })
      .join('\n');
  }

  /**
   * Re-inlines local image references as base64 data URLs so the exported
   * markdown is self-contained and can serve as the committed `tooltips.md`
   * source (the build then re-extracts the images). Images that are already
   * base64 pass through unchanged; references that cannot be fetched — the
   * legacy Flutter asset paths, or any 404 — are replaced with an italic
   * "Image unavailable" marker so nothing is silently dropped.
   */
  static async inlineTooltipImages(markdown: string): Promise<string> {
    const imageRegex = /!\[([^\]]*)\]\(([^)]+)\)/g;

    const urls = new Set<string>();
    for (const match of markdown.matchAll(imageRegex)) {
      const url = match[2]?.trim();
      if (url && !url.startsWith('data:')) {
        urls.add(url);
      }
    }

    // url -> base64 data URL, or null when it could not be fetched
    const resolved = new Map<string, string | null>();
    const queue = [...urls];
    const worker = async () => {
      for (let url = queue.shift(); url; url = queue.shift()) {
        resolved.set(url, await fetchImageAsDataUrl(url));
      }
    };
    await Promise.all(Array.from({ length: 6 }, worker));

    return markdown.replace(imageRegex, (full, alt: string, rawUrl: string) => {
      const url = rawUrl.trim();
      if (!url || url.startsWith('data:')) {
        return full;
      }
      const dataUrl = resolved.get(url);
      return dataUrl ? `![${alt}](${dataUrl})` : '*Image unavailable*';
    });
  }

  static normaliseTooltipKeyComponent(component: string): string {
    return component
      .toLowerCase()
      .trim()
      .replace(/[ ./]/g, '-')
      .replace(/-+/g, '-');
  }
}

const fetchImageAsDataUrl = async (url: string): Promise<string | null> => {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      return null;
    }
    const blob = await response.blob();
    // Must be an actual image. Local dev servers rewrite missing asset paths
    // to index.html and return it with a 200 status, so a non-image
    // content-type here means the reference is broken — treat it as
    // unavailable rather than base64-encoding an HTML page as an image.
    const contentType = (
      blob.type ||
      response.headers.get('content-type') ||
      ''
    ).toLowerCase();
    if (!contentType.startsWith('image/')) {
      return null;
    }
    const [dataUrl] = await FileHelpers.getFilesAsDataUrl([
      new File([blob], 'image', { type: contentType }),
    ]);
    return dataUrl ?? null;
  } catch {
    return null;
  }
};

// Dashes and quotes have reached tooltips as invisible control characters:
// U+2013/U+201C/U+201D cut down to their low byte (C0), or Windows-1252 bytes
// read as Latin-1 (C1). The file uses straight quotes.
const MISENCODED_PUNCTUATION = new Map([
  ['\u0013', '–'],
  ['\u0014', '—'],
  ['\u0018', "'"],
  ['\u0019', "'"],
  ['\u001c', '"'],
  ['\u001d', '"'],
  ['\u0091', "'"],
  ['\u0092', "'"],
  ['\u0093', '"'],
  ['\u0094', '"'],
  ['\u0096', '–'],
  ['\u0097', '—'],
]);

const repairControlCharacters = (text: string) =>
  text.replace(
    /[^\P{Cc}\t\n]/gu,
    (char) => MISENCODED_PUNCTUATION.get(char) ?? '',
  );

const setClassesForElementType = (
  fragment: HTMLDivElement,
  tagName: string,
  classes: string,
) => {
  const elements = fragment.getElementsByTagName(tagName);
  for (const element of elements) {
    element.className = classes;
  }
};
